import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getBackendURL } from '../utils/backendURL';
import { 
  X, 
  Send, 
  AlertCircle, 
  CheckCircle, 
  Headphones,
  Bug,
  Lightbulb,
  CreditCard,
  User,
  HelpCircle,
  Zap
} from 'lucide-react';

const SupportTicket = ({ isOpen, onClose, user }) => {
  const BACKEND_URL = getBackendURL();
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category: 'general_support',
    priority: 'medium',
    user_email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');

  // Categories with icons and descriptions
  const categories = {
    bug_report: {
      label: 'Bug Report',
      icon: Bug,
      description: 'Report a bug or technical issue',
      color: 'text-red-600'
    },
    feature_request: {
      label: 'Feature Request',
      icon: Lightbulb,
      description: 'Suggest a new feature or improvement',
      color: 'text-yellow-600'
    },
    api_issue: {
      label: 'API Issue',
      icon: Zap,
      description: 'Problems with API testing functionality',
      color: 'text-blue-600'
    },
    flow_testing_issue: {
      label: 'Flow Testing Issue',
      icon: Zap,
      description: 'Issues with Flow Testing automation',
      color: 'text-purple-600'
    },
    billing_question: {
      label: 'Billing Question',
      icon: CreditCard,
      description: 'Questions about billing and payments',
      color: 'text-green-600'
    },
    account_issue: {
      label: 'Account Issue',
      icon: User,
      description: 'Account access or settings problems',
      color: 'text-indigo-600'
    },
    general_support: {
      label: 'General Support',
      icon: Headphones,
      description: 'General help and questions',
      color: 'text-gray-600'
    },
    other: {
      label: 'Other',
      icon: HelpCircle,
      description: 'Something else not covered above',
      color: 'text-gray-500'
    }
  };

  const priorities = {
    low: { label: 'Low', color: 'text-green-600' },
    medium: { label: 'Medium', color: 'text-yellow-600' },
    high: { label: 'High', color: 'text-orange-600' },
    urgent: { label: 'Urgent', color: 'text-red-600' }
  };

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        subject: '',
        description: '',
        category: 'general_support',
        priority: 'medium',
        user_email: user?.email || ''
      });
      setError('');
      setSubmitSuccess(false);
    }
  }, [isOpen, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.description.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    if (!user && !formData.user_email.trim()) {
      setError('Please provide your email address');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      await axios.post(`${BACKEND_URL}/api/support/tickets`, {
        subject: formData.subject.trim(),
        description: formData.description.trim(),
        category: formData.category,
        priority: formData.priority,
        user_email: !user ? formData.user_email.trim() : undefined
      }, { headers });

      setSubmitSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Failed to submit support ticket:', error);
      let errorMessage = 'Failed to submit support ticket';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : 'Submission failed';
      }
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-2">
            <Headphones className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Support Ticket</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {submitSuccess ? (
          /* Success State */
          <div className="p-6 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Ticket Submitted Successfully!</h3>
            <p className="text-gray-600">
              Thank you for contacting us. We'll review your ticket and get back to you as soon as possible.
            </p>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* User Info (if not authenticated) */}
            {!user && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={formData.user_email}
                  onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="your.email@example.com"
                />
              </div>
            )}

            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                What can we help you with? *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(categories).map(([key, category]) => {
                  const IconComponent = category.icon;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setFormData({ ...formData, category: key })}
                      className={`p-3 text-left rounded-lg border-2 transition-all ${
                        formData.category === key
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <IconComponent className={`h-5 w-5 mt-0.5 ${category.color}`} />
                        <div>
                          <div className="font-medium text-gray-900">{category.label}</div>
                          <div className="text-xs text-gray-500">{category.description}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <div className="flex space-x-3">
                {Object.entries(priorities).map(([key, priority]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFormData({ ...formData, priority: key })}
                    className={`px-4 py-2 rounded-lg border-2 transition-all ${
                      formData.priority === key
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <span className={`font-medium ${priority.color}`}>
                      {priority.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <input
                type="text"
                required
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Brief description of your issue..."
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Please provide as much detail as possible about your issue, including steps to reproduce if it's a bug..."
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center space-x-2 p-3 bg-red-50 text-red-700 rounded-lg">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    <span>Submit Ticket</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SupportTicket;